import { useGetBusinesses } from "src/hooks/useGetBusinesses";
import { useLensPlatformClient } from "./useLensPlatformClient";
import { type Business } from "lens-platform-sdk";
import { type PlanMetadata } from "src/services/plans";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { getCurrentSubscription } from "src/services/getCurrentSubscription";
import { getSubscriptionPlanMetaData } from "src/services/getSubscriptionPlanCode";
import { useQueries } from "@tanstack/react-query";

export type BusinessWithCurrentPlan = Business & {
  currentPlan: PlanMetadata | null;
};

export function useBusinessesWithCurrentPlan() {
  const lensPlatformClient = useLensPlatformClient();
  const lensTeamLicenseFlag = useBooleanFlagValue("lens-team-license", false);
  const { businesses, loading: loadingBusinesses, error: errorLoadingBusinesses } = useGetBusinesses();

  const results = useQueries({
    queries: businesses.map((business) => ({
      queryKey: ["businessPlan", business.id, lensTeamLicenseFlag],
      queryFn: async () => {
        const subscriptions = await lensPlatformClient.business.getSubscriptions(business.id);

        const currentSubscription = getCurrentSubscription(subscriptions);
        const planMetaData = getSubscriptionPlanMetaData(currentSubscription, lensTeamLicenseFlag);

        return { ...business, currentPlan: planMetaData } as BusinessWithCurrentPlan;
      },
      enabled: businesses.length > 0,
    })),
  });

  return {
    businesses: results.map((result) => result.data).filter(Boolean) as BusinessWithCurrentPlan[],
    loading: loadingBusinesses || results.some((result) => result.isLoading),
    error: errorLoadingBusinesses || results.find((result) => result.error)?.error,
  };
}
